import { Input } from '@/components/ui';
import { formatPhoneInput } from '@/lib/phoneUtils';
import { PHONE_TYPES } from '../types';
import type { PhoneType, PhoneFormItem } from '../types';

interface PhoneInputProps {
  phone: PhoneFormItem;
  onChange: (phone: PhoneFormItem) => void;
  onRemove?: () => void;
  showRemove?: boolean;
  showPrimaryToggle?: boolean;
  showAdvanced?: boolean;
  disabled?: boolean;
  errors?: {
    phoneNumber?: string;
    phoneType?: string;
  };
  index?: number;
}

export function PhoneInput({
  phone,
  onChange,
  onRemove,
  showRemove = true,
  showPrimaryToggle = true,
  showAdvanced = false,
  disabled = false,
  errors = {},
  index,
}: PhoneInputProps) {
  const handlePhoneNumberChange = (value: string) => {
    // Format as user types
    const formatted = formatPhoneInput(value);
    onChange({ ...phone, phoneNumber: formatted });
  };

  const handleTypeChange = (value: PhoneType) => {
    onChange({ ...phone, phoneType: value });
  };

  const handlePrimaryChange = (checked: boolean) => {
    onChange({ ...phone, isPrimary: checked });
  };

  const handleCanSmsChange = (checked: boolean) => {
    onChange({ ...phone, canSms: checked });
  };

  const handleLabelChange = (value: string) => {
    onChange({ ...phone, label: value });
  };

  const handleNotesChange = (value: string) => {
    onChange({ ...phone, notes: value });
  };

  const phoneLabel = index !== undefined ? `Phone ${index + 1}` : 'Phone';

  return (
    <div className="p-3 bg-secondary-50 rounded-lg border border-secondary-200">
      {/* Phone Number and Type Row */}
      <div className="flex gap-3">
        {/* Phone Number */}
        <div className="flex-1">
          <Input
            label={phoneLabel}
            name={`phone-${phone.tempId}`}
            type="tel"
            value={phone.phoneNumber}
            onChange={(e) => handlePhoneNumberChange(e.target.value)}
            placeholder="(416) 555-1234"
            error={errors.phoneNumber}
            disabled={disabled}
          />
        </div>

        {/* Phone Type */}
        <div className="w-28">
          <label className="block text-sm font-medium text-secondary-700 mb-1">
            Type
          </label>
          <select
            value={phone.phoneType}
            onChange={(e) => handleTypeChange(e.target.value as PhoneType)}
            className="w-full px-3 py-2 border border-secondary-300 rounded-lg
                       focus:outline-none focus:ring-2 focus:ring-primary-500
                       focus:border-transparent text-sm"
            disabled={disabled}
          >
            {PHONE_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {/* Remove Button */}
        {showRemove && onRemove && (
          <div className="flex items-end pb-1">
            <button
              type="button"
              onClick={onRemove}
              className="p-2 text-secondary-400 hover:text-red-500
                         hover:bg-red-50 rounded-lg transition-colors"
              disabled={disabled}
              aria-label="Remove phone"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Options Row */}
      <div className="flex flex-wrap items-center gap-4 mt-3">
        {/* Primary Checkbox */}
        {showPrimaryToggle && (
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={phone.isPrimary}
              onChange={(e) => handlePrimaryChange(e.target.checked)}
              className="w-4 h-4 text-primary-600 border-secondary-300
                         rounded focus:ring-primary-500"
              disabled={disabled}
            />
            <span className="text-secondary-700">Primary</span>
          </label>
        )}

        {/* Can SMS Checkbox */}
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={phone.canSms}
            onChange={(e) => handleCanSmsChange(e.target.checked)}
            className="w-4 h-4 text-primary-600 border-secondary-300
                       rounded focus:ring-primary-500"
            disabled={disabled}
          />
          <span className="text-secondary-700">Can receive SMS</span>
        </label>
      </div>

      {/* Advanced Options (Label and Notes) */}
      {showAdvanced && (
        <div className="mt-3 pt-3 border-t border-secondary-200 space-y-3">
          <Input
            label="Label (optional)"
            name={`phone-label-${phone.tempId}`}
            value={phone.label}
            onChange={(e) => handleLabelChange(e.target.value)}
            placeholder="e.g., John's cell, Office direct"
            disabled={disabled}
          />
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1">
              Notes (optional)
            </label>
            <textarea
              value={phone.notes}
              onChange={(e) => handleNotesChange(e.target.value)}
              placeholder="e.g., Best after 6pm, Don't call during work hours"
              className="w-full px-3 py-2 border border-secondary-300 rounded-lg
                         focus:outline-none focus:ring-2 focus:ring-primary-500
                         focus:border-transparent text-sm resize-none"
              rows={2}
              disabled={disabled}
            />
          </div>
        </div>
      )}
    </div>
  );
}
